import { NgIf } from '@angular/common';
import { Component, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { pageTransitions } from '../../animations/page-transitions';
import { ColumnDefAndHeader, DatacenterType, UserDatacenterView } from '../../models/datacenter-aggrid-view';
import { FilterType } from '../../models/datacenter-filters';
import { User } from '../../models/user';
import { DatacenterFilterUtilsService } from '../../services/datacenter-filter-utils.service';
import { DatacenterFilterValidationService } from '../../services/datacenter-filter-validation.service';
import { DatacenterViewService } from '../../services/datacenter-view.service';
import { CustomLocation } from '../../services/location.custom';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import { DatacenterDefaultConfiguration } from '../datacenter-default-configuration';
import { DatacenterFiltersConfiguration } from '../datacenter-filters-configuration';
import { ColumnAggregator } from '../save-layout/column-aggregator';
import {
	DatacenterSaveLayoutComponent
} from '../save-layout/datacenter-save-layout.component';
import {
	DatacenterSaveViewDialogComponent, SaveViewDialogData
} from '../save-layout/datacenter-save-view-dialog.component';
import { DatacenterSortingComponent } from '../save-layout/datacenter-sorting.component';
import {
	DatacenterShareViewDialogComponent,
	ShareViewDialogData
} from '../share-view-layout/datacenter-share-view-dialog.component';

import { DatacenterFilterCustomizationComponent } from './datacenter-filter-customization.component';
import {
	DatacenterViewCancelDialogComponent
} from './datacenter-view-cancel-dialog.component';

@Component({
	selector: 'copo-datacenter-view-customization',
	templateUrl: './datacenter-view-customization.component.html',
	styleUrl: './datacenter-view-customization.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatButtonModule,
		MatIconModule,
		NgIf,
		TranslateModule,

		MatTabsModule,
		DatacenterFilterCustomizationComponent,
		DatacenterSaveLayoutComponent,
		DatacenterSortingComponent
	]
})
export class DatacenterViewCustomizationComponent implements OnDestroy {
	@Input() typeOfTab = 'layout';
	@Input() currentColumnStateForSorting?: ColumnAggregator;
	@ViewChild('layoutView') layoutView?: DatacenterSaveLayoutComponent;
	@ViewChild('sortingView') sortingView?: DatacenterSortingComponent;
	isLoading = false;
	datacenterTypeTitle = '';
	datacenterType: DatacenterType;
	user?: User;
	headerName = '';
	togglePosition = false;
	viewName: string;
	isDefaultView: boolean;
	isValid = true;
	filterLabel = '';
	layoutLabel = '';
	allFilters: FilterType[];
	addedFilters: FilterType[] = [];
	defaultFilters: FilterType[] = [];
	readonly viewToEdit: {
		currentView: UserDatacenterView;
		viewType: DatacenterType;
	};

	private dialogSub?: Subscription;
	private readonly queryParamsSub?: Subscription;
	private saveSub?: Subscription;
	private readonly validFilterSub?: Subscription;
	private readonly stateId = CustomLocation.GET_CURRENT_STATE_ID();
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly router: Router,
		private readonly route: ActivatedRoute,

		private readonly dialog: MatDialog,
		private readonly datacenterViewService: DatacenterViewService,
		private readonly translationService: TranslationService,
		private readonly orderDefaultConf: DatacenterDefaultConfiguration,
		private readonly filterValidationService: DatacenterFilterValidationService,
		private readonly defaultFilterService: DatacenterFiltersConfiguration,
		private readonly filterService: DatacenterFilterUtilsService,
		private readonly userService: UserService,
		private readonly snackBar: MatSnackBar
	) {
		this.user = this.userService.user;
		if (!this.datacenterViewService.viewToEdit) {
			this.datacenterViewService.viewToEdit = <typeof datacenterViewService.viewToEdit>
				CustomLocation.GET_STATE('viewToEdit', this.stateId);
		}
		const { viewToEdit } = this.datacenterViewService;
		const datacenterType =
			<DatacenterType | undefined> this.route.snapshot.queryParams['type'] ?? viewToEdit?.viewType;
		if (!datacenterType || !viewToEdit) {
			throw new Error('bad configuration');
		}
		this.datacenterType = datacenterType;
		this.viewToEdit = viewToEdit;
		this.filterLabel = translationService.getTR('datacenter.filters');
		this.layoutLabel = translationService.getTR('datacenter.layout-sorting');
		this.viewName = viewToEdit.currentView.name;
		this.isDefaultView = Boolean(viewToEdit.currentView.isDefault);
		this.allFilters = this.defaultFilterService.getAllFilters(viewToEdit.viewType);
		this.addedFilters = this.filterService.getPreparedFilters(viewToEdit.currentView);
		const existingAppliedFilters = new Set<string>(this.addedFilters.map(filter => filter.filterId));
		const defaultFilters = this.defaultFilterService.getDefaultFilter(viewToEdit.viewType);
		this.defaultFilters = existingAppliedFilters.size > 0
			? defaultFilters.filter(defaultFilter => !existingAppliedFilters.has(defaultFilter.filterId))
			: defaultFilters;
		this.datacenterTypeTitle = `datacenter.${ datacenterType }`;
		this.headerName = `${
			this.translationService.getTR('datacenter.data-center')
		} - ${
			this.translationService.getTR(this.datacenterTypeTitle)
		} - ${
			this.translationService.getTR('datacenter.edit-view')
		} "${ this.viewName === 'Default'
			? this.translationService.getTR('datacenter.default')
			: this.viewName }"`;
		this.validFilterSub = this.filterValidationService.getIsValidObs({
			next: value => {
				this.isValid = value;
			}
		});
		const currentNavigation = this.router.getCurrentNavigation();
		this.previousState = currentNavigation?.extras.state ?? currentNavigation?.previousNavigation?.extras.state;
	}

	toggleFavorite() {
		if (this.isDefaultView) {
			return;
		}
		const current = Boolean(this.viewToEdit.currentView.isFavorite);
		if (!current) {
			const favoritesCount = (this.user?.datacenterViews ?? [])
				.filter(v => v.type === this.datacenterType && v.isFavorite).length;
			if (favoritesCount >= 5) {
				const maxReachedMsg = this.translationService.getTR('datacenter.favorite.max-reached');
				this.snackBar.open(maxReachedMsg, undefined, { duration: 5000 });
				return;
			}
		}

		const updatedView: UserDatacenterView = {
			...this.viewToEdit.currentView,
			isFavorite: !current
		};
		this.isLoading = true;
		this.saveSub = this.datacenterViewService.saveUserView(updatedView).subscribe({
			next: async () => {
				await this.datacenterViewService.updateUserData();
				this.viewToEdit.currentView.isFavorite = !current;
				this.isLoading = false;
				const message = !current
					? this.translationService.getTR('datacenter.favorite.added')
					: this.translationService.getTR('datacenter.favorite.removed');
				this.snackBar.open(message, undefined, { duration: 3000 });
			},
			error: () => {
				this.isLoading = false;
				const updateErrorMsg = this.translationService.getTR('datacenter.favorite.update-error');
				this.snackBar.open(updateErrorMsg, undefined, { duration: 5000 });
			}
		});
	}

	ngOnDestroy() {
		CustomLocation.SET_STATE(
			'viewToEdit',
			this.stateId,
			this.datacenterViewService.viewToEdit
		);
		if (this.dialogSub) {
			this.dialogSub.unsubscribe();
		}
		if (this.queryParamsSub) {
			this.queryParamsSub.unsubscribe();
		}
		if (this.saveSub) {
			this.saveSub.unsubscribe();
		}
		if (this.validFilterSub) {
			this.validFilterSub.unsubscribe();
		}
	}

	openSaveDialog() {
		const dialogRef = this.dialog.open<DatacenterSaveViewDialogComponent, SaveViewDialogData, string | undefined>(
			DatacenterSaveViewDialogComponent,
			{
				panelClass: 'save-view-block',
				data: {
					name: this.viewName === 'Default' ? undefined : '',
					datacenterType: this.datacenterType
				},
				disableClose: true,
			}
		);

		this.dialogSub = dialogRef.afterClosed().subscribe(result => {
			if (result === undefined) {
				return;
			}
			this.createView();
			if (result) {
				this.saveAsView(result);
			} else {
				this.saveView();
			}
		});
	}

	setToggle($event: string) {
		this.typeOfTab = $event;
	}

	setTogglePosition($event: boolean) {
		this.togglePosition = $event;
	}

	setColumn($event: ColumnAggregator) {
		this.currentColumnStateForSorting = $event;
		if (this.sortingView) {
			this.sortingView.checkIfItemsWasMoved($event.allColumns);
		}
	}

	async close(newView: UserDatacenterView) {
		await this.router.navigate(['../'], {
			relativeTo: this.route,
			state: { ...this.previousState, view: newView }
		});
	}

	async back() {
		const state: Record<string, unknown> = { ...this.previousState };
		const previousView = <UserDatacenterView | undefined> state['view'];
		if (previousView) {
			state['view'] = { ...previousView, isFavorite: Boolean(this.viewToEdit.currentView.isFavorite) };
		}
		await this.router.navigate(['../'], { relativeTo: this.route, state });
	}

	saveView() {
		this.isLoading = true;
		const newView: UserDatacenterView = {
			id: this.viewToEdit.currentView.id,
			name: this.viewToEdit.currentView.name,
			isFavorite: Boolean(this.viewToEdit.currentView.isFavorite),
			type: this.viewToEdit.viewType,
			columnState: this.viewToEdit.currentView.columnState,
			sortModel: this.viewToEdit.currentView.sortModel,
			filterState: this.viewToEdit.currentView.filterState,
		};
		this.saveSub = this.datacenterViewService.saveUserView(newView).subscribe(async value => {
			newView.id = Number(value);
			await this.datacenterViewService.updateUserData();
			await this.close(newView);
		});
	}

	saveAsView(viewName?: string) {
		if (!viewName) {
			return;
		}
		this.isLoading = true;
		const newView: Omit<UserDatacenterView, 'id'> & Pick<Partial<UserDatacenterView>, 'id'> = {
			name: viewName,
			isFavorite: false,
			type: this.viewToEdit.viewType,
			columnState: this.viewToEdit.currentView.columnState,
			sortModel: this.viewToEdit.currentView.sortModel,
			filterState: this.viewToEdit.currentView.filterState,
		};
		this.saveSub = this.datacenterViewService.saveUserView(newView).subscribe(async value => {
			await this.datacenterViewService.updateUserData();
			await this.close({
				...newView,
				id: Number(value)
			});
		});
	}

	removeUserView() {
		const dialogRef = this.dialog.open<DatacenterViewCancelDialogComponent, undefined, boolean | undefined>(
			DatacenterViewCancelDialogComponent
		);

		this.dialogSub = dialogRef.afterClosed().subscribe(async result => {
			if (!result || !this.viewToEdit.currentView.id) {
				return;
			}
			this.isLoading = true;
			await this.datacenterViewService.removeUserView(this.viewToEdit.currentView.id);
			const defaultView = this.orderDefaultConf.getDefaultView(this.viewToEdit.viewType);
			await this.close(defaultView);
		});
	}

	shareUserView() {
		const dialogRef = this.dialog.open<DatacenterShareViewDialogComponent, ShareViewDialogData, string | undefined>(
			DatacenterShareViewDialogComponent,
			{
				panelClass: 'share-view-block',
				data: {
					name: this.viewName || '',
					datacenterType: this.datacenterType
				},
				disableClose: true,
			}
		);
		this.dialogSub = dialogRef.afterClosed().subscribe(result => {
			if (result === undefined) {
				return;
			}
			if (result) {
				this.createView();
				this.saveView();
			}
		});
	}

	getAppliedFilters() {
		if (this.addedFilters.length === 0) {
			return null;
		}
		return this.addedFilters.map(value => value.getFilters()).filter(filter => filter.values.length > 0);
	}

	checkForDefaultFiltersAndAddedFilters() {
		const filterMap = new Map<string, FilterType>();
		for (const filter of this.defaultFilters) {
			filterMap.set(filter.filterId, filter);
		}
		const addedFilterIds = new Set<string>();
		for (const addFilter of this.addedFilters) {
			addedFilterIds.add(addFilter.filterId);
			const matchedFilter = filterMap.get(addFilter.filterId);
			if (matchedFilter && matchedFilter.filterType === addFilter.filterType) {
				for (const value of matchedFilter.getValues()) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any
					addFilter.getValues().push(<any> value);
				}
			}
		}
		for (const [filterId, filter] of filterMap) {
			if (!addedFilterIds.has(filterId)) {
				this.addedFilters.push(filter);
				addedFilterIds.add(filterId);
			}
		}
	}

	async setCurrentView() {
		if (!this.layoutView || !this.sortingView) {
			return;
		}
		this.checkForDefaultFiltersAndAddedFilters();
		this.isLoading = true;
		const columnState = this.buildColumnState(this.layoutView);
		const newView: UserDatacenterView = {
			id: this.viewToEdit.currentView.id,
			name: this.viewToEdit.currentView.name,
			isFavorite: Boolean(this.viewToEdit.currentView.isFavorite),
			type: this.datacenterType,
			columnState,
			sortModel: this.sortingView.getSortModel(),
			filterState: this.getAppliedFilters(),
		};
		await this.close(newView);
	}

	getView() {
		return this.viewToEdit;
	}

	getPreviousState() {
		return this.previousState;
	}

	protected buildColumnState(layoutView: DatacenterSaveLayoutComponent) {
		const columnState = layoutView.getColumnState();
		const colIds = columnState.map(col => col.colId);
		const additionalColumns: ColumnDefAndHeader[] = [];
		const allColumns = this.orderDefaultConf.getAllColumnsAndHeader(this.datacenterType);
		// to maintain the order selected in customise view
		for (const colId of colIds) {
			const aggregaredFields = allColumns.filter(item => item.groupId !== undefined
				&& item.groupId === colId);
			for (const field of aggregaredFields) {
				additionalColumns.push({
					colId: field.colId,
					header: field.header,
					groupId: field.groupId
				});
			}
		}
		let index = 0;
		for (const colHeader of additionalColumns) {
			if (colHeader.groupId) {
				columnState.splice(colIds.indexOf(colHeader.groupId) + index, 0, colHeader);
			}
			index++;
		}
		return columnState;
	}

	private createView() {
		if (!this.layoutView || !this.sortingView) {
			return;
		}
		this.checkForDefaultFiltersAndAddedFilters();
		this.sortingView.checkIfItemsWasMoved(this.layoutView.columnAggregatorList.allColumns);
		const columnState = this.buildColumnState(this.layoutView);
		this.viewToEdit.currentView.columnState = columnState;
		this.viewToEdit.currentView.sortModel = this.sortingView.getSortModel();
		this.viewToEdit.currentView.filterState = this.getAppliedFilters();
	}
}
